/**
 * SuretyWithdrawalPanel — the motion that gets Lloyd off a bond he personally signed (Aug 25 2026).
 *
 * Four decisions worth knowing about:
 *
 *  1. **It says what the document does, at the top, every time.** This motion asks the court to
 *     issue a warrant for the firm's own client so the surety can surrender them. That is not a
 *     detail to discover after pressing the button, and it is why nothing in the system generates
 *     this on its own — a person chooses it, on a named case, for a stated reason.
 *  2. **The panel leads with the exposure, not with the form.** A case with no live bond has nothing
 *     to withdraw from, and it says so instead of showing fields that cannot produce anything.
 *  3. **The court override is visible, not buried.** After an appeal the case is in the county
 *     court while the court on our record is still the JP or municipal court it appealed out of —
 *     so on exactly the cases this motion is written for, the default caption is the wrong court.
 *  4. **Generating is not filing, and filing is not release.** The two date fields underneath the
 *     result exist because only the granted fiat takes the firm off the bond, and until it is
 *     recorded the closing letter on this case stays blocked.
 *  5. **The remittitur is an option here, not a panel of its own** (Sep 3 2026). It is the money half
 *     of the same act, and its own paragraph 2 tells the court both were filed together. A second
 *     panel would give staff two places that each produce a withdrawal motion, which is how a case
 *     ends up with two of them on file. Ticking the box calls the packet endpoint, which returns
 *     both documents or refuses with the reasons.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BondApi, { CaseBondDto } from '../../api/BondApi';
import SuretyWithdrawalApi, {
  WITHDRAWAL_GROUND_OPTIONS,
  WithdrawalBlockerDto,
  SuretyWithdrawalBlockedError,
  WithdrawalResultDto,
} from '../../api/SuretyWithdrawalApi';
import RemittiturApi, { RemittiturBlockedError } from '../../api/RemittiturApi';

interface SuretyWithdrawalPanelProps {
  Ticket_guid: string;
}

/** A base64 PDF the browser can open without the page having to host it anywhere. */
function pdfObjectUrl(base64: string): string {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
}

function dateOnly(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function describeBond(bond: CaseBondDto): string {
  const kind = bond.CaseBond_Type === 'ATTORNEY' ? 'Attorney bond' : 'Appeal bond';
  const filed = dateOnly(bond.CaseBond_FiledAt);
  const amount =
    typeof bond.CaseBond_AmountCents === 'number'
      ? ` — $${(bond.CaseBond_AmountCents / 100).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : '';
  return `${kind}, filed ${filed || 'date not recorded'}${amount}`;
}

/** Filed and not yet released — the same test the server applies, so the screen agrees with it. */
function isLive(bond: CaseBondDto): boolean {
  const surety = bond.CaseBond_Type === 'APPEAL' || bond.CaseBond_Type === 'ATTORNEY';
  return surety && !!bond.CaseBond_FiledAt && !bond.CaseBond_SuretyReleasedAt;
}

const SuretyWithdrawalPanel: React.FC<SuretyWithdrawalPanelProps> = ({ Ticket_guid }) => {
  const [bonds, setBonds] = useState<CaseBondDto[]>([]);
  const [loadingBonds, setLoadingBonds] = useState(true);

  const [ground, setGround] = useState('');
  const [bondGuid, setBondGuid] = useState('');
  const [missedSettingDate, setMissedSettingDate] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [amountDue, setAmountDue] = useState('');
  const [locateEfforts, setLocateEfforts] = useState('');
  const [offense, setOffense] = useState('');
  const [bondDate, setBondDate] = useState('');
  const [courtName, setCourtName] = useState('');
  const [signatureDate, setSignatureDate] = useState('');

  const [withRemittitur, setWithRemittitur] = useState(false);
  const [bondAmount, setBondAmount] = useState('');

  const [blockers, setBlockers] = useState<WithdrawalBlockerDto[]>([]);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<WithdrawalResultDto | null>(null);
  const [motionUrl, setMotionUrl] = useState<string | null>(null);
  const [remittiturUrl, setRemittiturUrl] = useState<string | null>(null);
  const [remittiturFileName, setRemittiturFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [recorded, setRecorded] = useState<string | null>(null);

  const loadBonds = useCallback(async () => {
    setLoadingBonds(true);
    try {
      const dto = await BondApi.list(Ticket_guid);
      setBonds(dto.bonds);
      const live = dto.bonds.filter(isLive);
      // Preselect the single live bond, which is the ordinary case. Where there are two, the server
      // refuses to guess and so does this.
      setBondGuid((current) => current || (live.length === 1 ? live[0].CaseBond_guid : ''));
    } catch {
      // Non-fatal: the generate call re-checks server-side, so a failed list must not lock the panel.
    } finally {
      setLoadingBonds(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    loadBonds();
  }, [loadBonds]);

  const loadReadiness = useCallback(async () => {
    try {
      // With the remittitur added the packet's readiness is the one that matters: it adds the
      // bond-amount blocker the withdrawal motion alone never checks, and it reports the
      // withdrawal's own blockers alongside its own, so ticking the box adds reasons rather than
      // replacing them.
      const dto = withRemittitur
        ? await RemittiturApi.readiness(Ticket_guid, {
            ground: ground || undefined,
            bondGuid: bondGuid || undefined,
          })
        : await SuretyWithdrawalApi.readiness(Ticket_guid, { ground: ground || undefined });
      setBlockers(dto.readiness.blockers);
    } catch {
      // Same reason as above — the preview is a convenience, the server is the authority.
    }
  }, [Ticket_guid, ground, withRemittitur, bondGuid]);

  useEffect(() => {
    loadReadiness();
  }, [loadReadiness]);

  useEffect(() => {
    return () => {
      if (motionUrl) URL.revokeObjectURL(motionUrl);
    };
  }, [motionUrl]);

  useEffect(() => {
    return () => {
      if (remittiturUrl) URL.revokeObjectURL(remittiturUrl);
    };
  }, [remittiturUrl]);

  const liveBonds = bonds.filter(isLive);
  const selectedBond = bonds.find((bond) => bond.CaseBond_guid === bondGuid) ?? null;
  const selectedGround = WITHDRAWAL_GROUND_OPTIONS.find((option) => option.value === ground);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setBlockers([]);
    setResult(null);
    setRemittiturUrl(null);
    setRemittiturFileName(null);
    setRecorded(null);

    const common = {
      ground,
      bondGuid: bondGuid || null,
      offense: offense || null,
      bondDate: bondDate || null,
      missedSettingDate: missedSettingDate || null,
      amountPaidCents: amountPaid === '' ? null : Math.round(Number(amountPaid) * 100),
      amountDueCents: amountDue === '' ? null : Math.round(Number(amountDue) * 100),
      locateEffortsDescription: locateEfforts || null,
      signatureDate: signatureDate || null,
      courtName: courtName || null,
    };

    try {
      if (withRemittitur) {
        // One call returns both, or refuses. There is no path here that produces the withdrawal
        // motion and leaves the remittitur behind, which is what the packet exists to prevent.
        const packet = await RemittiturApi.generatePacket(Ticket_guid, {
          ...common,
          bondAmountCents: bondAmount === '' ? null : Math.round(Number(bondAmount) * 100),
        });
        setResult({
          fileName: packet.withdrawal.fileName,
          pdfBase64: packet.withdrawal.pdfBase64,
          bondGuid: packet.bondGuid,
        });
        setMotionUrl(pdfObjectUrl(packet.withdrawal.pdfBase64));
        setRemittiturUrl(pdfObjectUrl(packet.remittitur.pdfBase64));
        setRemittiturFileName(packet.remittitur.fileName);
      } else {
        const dto = await SuretyWithdrawalApi.generate(Ticket_guid, common);
        setResult(dto);
        setMotionUrl(pdfObjectUrl(dto.pdfBase64));
      }
      await loadBonds();
      await loadReadiness();
    } catch (err) {
      if (err instanceof SuretyWithdrawalBlockedError || err instanceof RemittiturBlockedError) {
        setBlockers(err.blockers.length > 0 ? err.blockers : [{ field: null, reason: err.message }]);
      } else {
        setError(
          err instanceof Error ? err.message : 'Failed to generate the motion to withdraw as surety',
        );
      }
    } finally {
      setGenerating(false);
    }
  };

  const recordDate = async (
    field: 'CaseBond_WithdrawalMotionFiledAt' | 'CaseBond_SuretyReleasedAt',
    value: string,
    label: string,
  ) => {
    if (!selectedBond) return;
    setRecording(true);
    setError(null);
    try {
      await BondApi.update(selectedBond.CaseBond_guid, {
        [field]: value ? new Date(`${value}T00:00:00.000Z`).toISOString() : null,
      });
      setRecorded(label);
      await loadBonds();
      await loadReadiness();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record the date');
    } finally {
      setRecording(false);
    }
  };

  const canSubmit =
    ground !== '' &&
    !generating &&
    liveBonds.length > 0 &&
    (liveBonds.length === 1 || bondGuid !== '');

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        Motion to withdraw as surety
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        For a case where the firm has signed a bond and needs to come off it. Produces the motion and
        its fiat for a wet signature and a notary. It is not sent anywhere.
      </Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle>This motion asks the court to issue a warrant for the client</AlertTitle>
        Filing it asks the court&rsquo;s permission to surrender the defendant and to issue a warrant
        for their arrest. Nothing generates this automatically — it is produced only when someone
        decides to withdraw, on this case, for the reason chosen below.
      </Alert>

      {loadingBonds ? (
        <CircularProgress size={20} />
      ) : liveBonds.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>There is no live surety bond on this case</AlertTitle>
          {bonds.length === 0
            ? 'No bond has been generated here, so the firm is not standing surety on anything.'
            : 'Every bond on this case is either not recorded as filed, or already released. Record ' +
              'the filing date on the bond first if it has in fact been filed.'}
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>
            The firm is on {liveBonds.length === 1 ? 'this bond' : `${liveBonds.length} bonds`}
          </AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {liveBonds.map((bond) => (
              <li key={bond.CaseBond_guid}>
                {describeBond(bond)}
                {bond.CaseBond_WithdrawalMotionFiledAt && (
                  <Chip
                    size="small"
                    color="warning"
                    sx={{ ml: 1 }}
                    label={`motion filed ${dateOnly(bond.CaseBond_WithdrawalMotionFiledAt)}, not yet ruled on`}
                  />
                )}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {blockers.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>This case is not ready for a motion to withdraw as surety</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {blockers.map((blocker, index) => (
              <li key={blocker.field ?? `blocker-${index}`}>{blocker.reason}</li>
            ))}
          </ul>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2} sx={{ mb: 1 }}>
        {liveBonds.length > 1 && (
          <FormControl size="small" sx={{ maxWidth: 520 }}>
            <InputLabel id="surety-bond-label">Which bond</InputLabel>
            <Select
              labelId="surety-bond-label"
              label="Which bond"
              value={bondGuid}
              onChange={(e) => setBondGuid(e.target.value)}
            >
              {liveBonds.map((bond) => (
                <MenuItem key={bond.CaseBond_guid} value={bond.CaseBond_guid}>
                  {describeBond(bond)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ maxWidth: 480 }}>
          <InputLabel id="surety-ground-label">Ground</InputLabel>
          <Select
            labelId="surety-ground-label"
            label="Ground"
            value={ground}
            onChange={(e) => setGround(e.target.value)}
          >
            {WITHDRAWAL_GROUND_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedGround?.requires === 'missedSettingDate' && (
          <TextField
            label="Setting the defendant missed"
            type="date"
            size="small"
            value={missedSettingDate}
            onChange={(e) => setMissedSettingDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="The date the motion states. It comes from the appearance outcome on that setting."
            sx={{ maxWidth: 320 }}
          />
        )}

        {selectedGround?.requires === 'amounts' && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Amount paid"
              type="number"
              size="small"
              value={amountPaid}
              onChange={(e) => setAmountPaid(e.target.value)}
              helperText="Dollars. Zero is a real answer."
              sx={{ maxWidth: 200 }}
            />
            <TextField
              label="Total amount due"
              type="number"
              size="small"
              value={amountDue}
              onChange={(e) => setAmountDue(e.target.value)}
              helperText="Dollars still owed to the surety"
              sx={{ maxWidth: 200 }}
            />
          </Stack>
        )}

        {selectedGround?.requires === 'locateEfforts' && (
          <TextField
            label="What was done to find the defendant"
            size="small"
            multiline
            minRows={2}
            value={locateEfforts}
            onChange={(e) => setLocateEfforts(e.target.value)}
            helperText="A bare assertion gives the judge nothing to rule on. Say what was actually tried."
            sx={{ maxWidth: 640 }}
          />
        )}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
        <TextField
          label="Offence the bond was made for"
          size="small"
          value={offense}
          onChange={(e) => setOffense(e.target.value)}
          helperText="Defaults to the offence on the case"
          sx={{ maxWidth: 340 }}
        />
        <TextField
          label="Date the bond was made"
          type="date"
          size="small"
          value={bondDate}
          onChange={(e) => setBondDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="Defaults to the day the bond was filed"
          sx={{ maxWidth: 240 }}
        />
      </Stack>

      {/*
        Not hidden behind an "advanced" toggle, unlike the continuance motion's equivalent. An
        appealed case is in the county court and nothing re-points the court on our record when the
        bond is filed — so on the cases this motion exists for, the default is the court the case has
        already left, and a field nobody sees is a field nobody corrects.
      */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Court this is filed in"
          size="small"
          value={courtName}
          onChange={(e) => setCourtName(e.target.value)}
          helperText="After an appeal this is the county court, not the court on the case"
          sx={{ maxWidth: 360 }}
        />
        <TextField
          label="Signature date"
          type="date"
          size="small"
          value={signatureDate}
          onChange={(e) => setSignatureDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="Defaults to today; the jurat follows it"
          sx={{ maxWidth: 220 }}
        />
      </Stack>

      {/*
        The remittitur rides with the withdrawal rather than living on its own panel. Two panels
        would give staff two places that each produce a withdrawal motion, which is how a case ends
        up with two of them filed. The server enforces the same thing: the packet endpoint returns
        both documents or refuses.
      */}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={withRemittitur}
              onChange={(e) => setWithRemittitur(e.target.checked)}
            />
          }
          label="Also file the motion for remittitur, to claim the bond back if it is forfeited"
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -0.5 }}>
          Filed at the same time as the withdrawal, under Article 22.16. It asks the court to return
          the bond, less costs, once the defendant is surrendered. Both documents generate together
          or neither does.
        </Typography>

        {withRemittitur && (
          <TextField
            label="Bond amount"
            type="number"
            size="small"
            value={bondAmount}
            onChange={(e) => setBondAmount(e.target.value)}
            helperText="Dollars. Defaults to the amount recorded on the bond; the remittitur is refused without one."
            sx={{ maxWidth: 320, mt: 2, ml: 4 }}
          />
        )}
      </Box>

      <Box>
        <Button
          variant="contained"
          color="warning"
          onClick={handleGenerate}
          disabled={!canSubmit}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {generating
            ? 'Generating…'
            : withRemittitur
              ? 'Generate both motions'
              : 'Generate motion to withdraw'}
        </Button>
      </Box>

      {result && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <AlertTitle>
            {remittiturUrl
              ? 'Both motions generated — not filed, and not sent'
              : 'Motion generated — not filed, and not sent'}
          </AlertTitle>
          <Stack spacing={0.5}>
            {motionUrl && (
              <MuiLink href={motionUrl} target="_blank" rel="noopener noreferrer">
                Open {result.fileName}
              </MuiLink>
            )}
            {remittiturUrl && remittiturFileName && (
              <MuiLink href={remittiturUrl} target="_blank" rel="noopener noreferrer">
                Open {remittiturFileName}
              </MuiLink>
            )}
          </Stack>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {remittiturUrl
              ? 'Both need a wet signature and a notary, and they are filed together. The ' +
                'remittitur tells the court the withdrawal motion was filed at the same time.'
              : 'It needs a wet signature and a notary before it goes to the court.'}
          </Typography>
        </Alert>
      )}

      {selectedBond && (result || selectedBond.CaseBond_WithdrawalMotionGeneratedAt) && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Record what has happened to it
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The firm stays on the bond until the court grants the motion. Until the release is
            recorded here, a closing letter on this case will be refused.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <TextField
              label="Motion filed with the court"
              type="date"
              size="small"
              defaultValue={dateOnly(selectedBond.CaseBond_WithdrawalMotionFiledAt)}
              onChange={(e) =>
                recordDate('CaseBond_WithdrawalMotionFiledAt', e.target.value, 'Motion filing date recorded')
              }
              InputLabelProps={{ shrink: true }}
              disabled={recording}
              sx={{ maxWidth: 260 }}
            />
            <TextField
              label="Court granted it — surety released"
              type="date"
              size="small"
              defaultValue={dateOnly(selectedBond.CaseBond_SuretyReleasedAt)}
              onChange={(e) =>
                recordDate('CaseBond_SuretyReleasedAt', e.target.value, 'Release recorded — the firm is off this bond')
              }
              InputLabelProps={{ shrink: true }}
              disabled={recording}
              sx={{ maxWidth: 300 }}
            />
          </Stack>
          {recorded && (
            <Alert severity="success" sx={{ mt: 1 }}>
              {recorded}
            </Alert>
          )}
        </Box>
      )}

      <Divider sx={{ my: 3 }} />
    </Box>
  );
};

export default SuretyWithdrawalPanel;
